import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = '';
const TC = '#0d47a1';
const TB = '#e3f2fd';
const CATEGORIES = ['전체','규정/정책','매뉴얼/가이드','보고서','계약서','회의록','교육자료','기술문서','기타'];

function KnowledgeAgent() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('search'); // search, docs, upload, history
  const [loading, setLoading] = useState(false);

  // 검색
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState([]);
  const [searchCat, setSearchCat] = useState('전체');
  const [chatHistory, setChatHistory] = useState([]);

  // 문서 관리
  const [docs, setDocs] = useState([]);
  const [docCat, setDocCat] = useState('전체');
  const [docSearch, setDocSearch] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);

  // 업로드
  const [uploadCat, setUploadCat] = useState('기타');
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const multiFileRef = useRef(null);

  // Q&A 히스토리
  const [qnaList, setQnaList] = useState([]);

  // 통계
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (tab === 'docs') loadDocs();
    if (tab === 'history') loadQna();
  }, [tab, docCat, docSearch]);

  const loadStats = async () => {
    try { const r = await fetch(`${API}/knowledge/stats`); setStats(await r.json()); } catch(e) {}
  };

  const loadDocs = async () => {
    try {
      const params = new URLSearchParams();
      if (docCat && docCat !== '전체') params.set('category', docCat);
      if (docSearch) params.set('search', docSearch);
      const r = await fetch(`${API}/knowledge/docs?${params}`);
      setDocs(await r.json());
    } catch(e) {}
  };

  const loadQna = async () => {
    try { const r = await fetch(`${API}/knowledge/qna-history`); setQnaList(await r.json()); } catch(e) {}
  };

  const handleAsk = async () => {
    if (!question.trim()) return;
    setLoading(true); setAnswer(''); setSources([]);
    setChatHistory(prev => [...prev, { type: 'q', text: question }]);
    try {
      const res = await fetch(`${API}/knowledge/ask`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, category: searchCat }),
      });
      const data = await res.json();
      setAnswer(data.answer || '');
      setSources(data.sources || []);
      setChatHistory(prev => [...prev, { type: 'a', text: data.answer || '', sources: data.sources || [] }]);
    } catch(e) {
      setAnswer('검색 실패');
      setChatHistory(prev => [...prev, { type: 'a', text: '검색 실패', sources: [] }]);
    }
    setQuestion('');
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setUploadResult(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', uploadCat);
    try {
      const res = await fetch(`${API}/knowledge/upload`, { method: 'POST', body: fd });
      const data = await res.json();
      setUploadResult(data);
      loadStats();
    } catch(e) {
      setUploadResult({ error: '업로드 실패' });
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleMultiUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setUploading(true); setUploadResult(null);
    const fd = new FormData();
    for (let i = 0; i < files.length; i++) fd.append('files', files[i]);
    fd.append('category', uploadCat);
    try {
      const res = await fetch(`${API}/knowledge/upload-multi`, { method: 'POST', body: fd });
      const data = await res.json();
      setUploadResult(data);
      loadStats();
    } catch(e) {
      setUploadResult({ error: '업로드 실패' });
    }
    setUploading(false);
    if (multiFileRef.current) multiFileRef.current.value = '';
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await fetch(`${API}/knowledge/docs/${id}`, { method: 'DELETE' });
      loadDocs(); loadStats();
    } catch(e) {}
  };

  const handleViewDoc = async (id) => {
    try {
      const res = await fetch(`${API}/knowledge/docs/${id}`);
      setSelectedDoc(await res.json());
    } catch(e) {}
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const QUICK_Q = [
    '최근 등록된 규정 변경사항은?',
    '휴가 신청 절차를 알려줘',
    '보안 정책 요약해줘',
    '신규 입사자 온보딩 가이드',
  ];

  const mdC = {
    table: ({children}) => <table style={{borderCollapse:'collapse',width:'100%',margin:'12px 0',fontSize:14}}>{children}</table>,
    thead: ({children}) => <thead style={{background:TB}}>{children}</thead>,
    th: ({children}) => <th style={{border:'1px solid #bbdefb',padding:'8px 12px',fontWeight:'bold',textAlign:'left',color:TC}}>{children}</th>,
    td: ({children}) => <td style={{border:'1px solid #e0e0e0',padding:'8px 12px'}}>{children}</td>,
    h1: ({children}) => <h1 style={{color:TC,borderBottom:`2px solid ${TC}`,paddingBottom:8,marginTop:20,fontSize:20}}>{children}</h1>,
    h2: ({children}) => <h2 style={{color:TC,borderLeft:`4px solid ${TC}`,paddingLeft:12,marginTop:16,fontSize:17}}>{children}</h2>,
    h3: ({children}) => <h3 style={{color:'#1565c0',marginTop:12,fontSize:15}}>{children}</h3>,
    strong: ({children}) => <strong style={{color:'#0d47a1'}}>{children}</strong>,
    li: ({children}) => <li style={{marginBottom:4,lineHeight:1.7}}>{children}</li>,
  };

  const catIcon = (cat) => {
    const map = {
      '규정/정책': {bg:'#fff3e0',color:'#e65100',icon:'P'},
      '매뉴얼/가이드': {bg:'#e8f5e9',color:'#2e7d32',icon:'M'},
      '보고서': {bg:'#e3f2fd',color:'#1565c0',icon:'R'},
      '계약서': {bg:'#fce4ec',color:'#c62828',icon:'C'},
      '회의록': {bg:'#e0f2f1',color:'#00695c',icon:'W'},
      '교육자료': {bg:'#f3e8fd',color:'#7b1fa2',icon:'E'},
      '기술문서': {bg:'#e8eaf6',color:'#283593',icon:'T'},
      '기타': {bg:'#f5f5f5',color:'#616161',icon:'D'},
    };
    return map[cat] || map['기타'];
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {/* 헤더 */}
      <div style={{ background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%)', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', width: 36, height: 36, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M13 4l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>CLSOFTLAB AI SERVICE</div>
          <h1 style={{ color: 'white', fontSize: 20, margin: 0, fontWeight: 700 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{verticalAlign:'middle',marginRight:8}}>
              <circle cx="11" cy="11" r="8" stroke="white" strokeWidth="2"/><path d="M21 21l-4.35-4.35" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            사내 지식 검색
          </h1>
        </div>

        {/* 통계 뱃지 */}
        {stats && (
          <div style={{ display: 'flex', gap: 12 }}>
            {[
              { label: '문서', value: stats.total_docs },
              { label: '페이지', value: stats.total_pages },
              { label: 'Q&A', value: stats.total_qna },
            ].map((s, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.15)', padding: '4px 12px', borderRadius: 6, textAlign: 'center' }}>
                <div style={{ color: 'white', fontSize: 16, fontWeight: 700 }}>{s.value}</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* 탭 */}
        <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 3 }}>
          {[
            { key: 'search', label: '지식 검색' },
            { key: 'docs', label: '문서 관리' },
            { key: 'upload', label: '문서 등록' },
            { key: 'history', label: 'Q&A 기록' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: tab === t.key ? 'white' : 'transparent',
              color: tab === t.key ? TC : 'rgba(255,255,255,0.8)',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 20px 60px' }}>

        {/* === 지식 검색 탭 === */}
        {tab === 'search' && (
          <div>
            {/* 카테고리 필터 */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setSearchCat(c)} style={{
                  padding: '5px 14px', borderRadius: 20, border: `1px solid ${searchCat === c ? TC : '#ddd'}`,
                  background: searchCat === c ? TB : 'white', color: searchCat === c ? TC : '#666',
                  fontSize: 13, cursor: 'pointer', fontWeight: searchCat === c ? 600 : 400,
                }}>{c}</button>
              ))}
            </div>

            {/* 대화 영역 */}
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e0e0e0', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
              {/* 채팅 히스토리 */}
              <div style={{ flex: 1, padding: 20, overflowY: 'auto', maxHeight: 500 }}>
                {chatHistory.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <div style={{ width: 80, height: 80, borderRadius: '50%', background: TB, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="8" stroke={TC} strokeWidth="2"/><path d="M21 21l-4.35-4.35" stroke={TC} strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <h3 style={{ color: '#333', margin: '0 0 8px', fontSize: 18 }}>사내 문서 기반 AI 검색</h3>
                    <p style={{ color: '#888', fontSize: 14, marginBottom: 20 }}>등록된 문서를 기반으로 질문에 답변합니다</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {QUICK_Q.map((q, i) => (
                        <button key={i} onClick={() => { setQuestion(q); }} style={{
                          padding: '8px 16px', borderRadius: 20, border: '1px solid #e0e0e0',
                          background: 'white', color: '#555', fontSize: 13, cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = TC; e.currentTarget.style.background = TB; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = 'white'; }}
                        >{q}</button>
                      ))}
                    </div>
                  </div>
                )}

                {chatHistory.map((msg, i) => (
                  <div key={i} style={{ marginBottom: 16, display: 'flex', flexDirection: msg.type === 'q' ? 'row-reverse' : 'row', gap: 10 }}>
                    {msg.type === 'a' && (
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: TB, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke={TC} strokeWidth="2"/><path d="M21 21l-4-4" stroke={TC} strokeWidth="2"/></svg>
                      </div>
                    )}
                    <div style={{
                      maxWidth: '75%', padding: '12px 16px', borderRadius: 12,
                      background: msg.type === 'q' ? TC : '#f8f9fa',
                      color: msg.type === 'q' ? 'white' : '#333',
                      fontSize: 14, lineHeight: 1.7,
                      borderTopRightRadius: msg.type === 'q' ? 4 : 12,
                      borderTopLeftRadius: msg.type === 'a' ? 4 : 12,
                    }}>
                      {msg.type === 'q' ? msg.text : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdC}>{msg.text}</ReactMarkdown>
                      )}
                      {msg.sources && msg.sources.length > 0 && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e0e0e0' }}>
                          <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>참고 문서:</div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {msg.sources.map((s, j) => (
                              <span key={j} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: TB, color: TC, fontWeight: 500 }}>
                                {s.filename} ({s.similarity}%)
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: TB, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke={TC} strokeWidth="2"/><path d="M21 21l-4-4" stroke={TC} strokeWidth="2"/></svg>
                    </div>
                    <div style={{ padding: '12px 16px', borderRadius: 12, background: '#f8f9fa', display: 'flex', gap: 6, alignItems: 'center' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: TC, animation: 'bounce 1.4s infinite' }}/>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: TC, animation: 'bounce 1.4s infinite 0.2s' }}/>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: TC, animation: 'bounce 1.4s infinite 0.4s' }}/>
                    </div>
                  </div>
                )}
              </div>

              {/* 입력 바 */}
              <div style={{ padding: '12px 16px', borderTop: '1px solid #e0e0e0', display: 'flex', gap: 10 }}>
                <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="사내 문서에서 궁금한 점을 질문하세요..."
                  onKeyDown={e => e.key === 'Enter' && !loading && handleAsk()}
                  style={{ flex: 1, padding: '11px 16px', borderRadius: 10, border: '1px solid #ddd', fontSize: 14 }} />
                <button onClick={handleAsk} disabled={loading} style={{
                  padding: '11px 24px', borderRadius: 10, border: 'none', cursor: loading ? 'default' : 'pointer',
                  background: loading ? '#bbb' : TC, color: 'white', fontSize: 14, fontWeight: 600,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{verticalAlign:'middle'}}>
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
            <style>{`@keyframes bounce { 0%,80%,100% { transform: translateY(0); } 40% { transform: translateY(-8px); } }`}</style>
          </div>
        )}

        {/* === 문서 관리 탭 === */}
        {tab === 'docs' && (
          <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <input value={docSearch} onChange={e => setDocSearch(e.target.value)} placeholder="문서명 또는 내용 검색..."
                style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, minWidth: 250 }} />
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {CATEGORIES.map(c => (
                  <button key={c} onClick={() => setDocCat(c)} style={{
                    padding: '5px 12px', borderRadius: 16, border: `1px solid ${docCat === c ? TC : '#ddd'}`,
                    background: docCat === c ? TB : 'white', color: docCat === c ? TC : '#888', fontSize: 12, cursor: 'pointer', fontWeight: docCat === c ? 600 : 400,
                  }}>{c}</button>
                ))}
              </div>
              <span style={{ color: '#888', fontSize: 13, marginLeft: 'auto' }}>{docs.length}개 문서</span>
            </div>

            {selectedDoc && (
              <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16, color: '#333' }}>{selectedDoc.filename}</h3>
                  <button onClick={() => setSelectedDoc(null)} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: 20 }}>x</button>
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 12, background: catIcon(selectedDoc.category).bg, color: catIcon(selectedDoc.category).color, fontWeight: 600 }}>{selectedDoc.category}</span>
                  <span style={{ fontSize: 12, color: '#888' }}>{formatSize(selectedDoc.file_size)}</span>
                  <span style={{ fontSize: 12, color: '#888' }}>{selectedDoc.page_count}페이지</span>
                </div>
                {selectedDoc.summary && (
                  <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 14, marginBottom: 12, fontSize: 13, lineHeight: 1.7, color: '#555' }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdC}>{selectedDoc.summary}</ReactMarkdown>
                  </div>
                )}
                <details>
                  <summary style={{ cursor: 'pointer', color: TC, fontWeight: 600, fontSize: 13 }}>전체 내용 보기</summary>
                  <pre style={{ background: '#f8f9fa', padding: 14, borderRadius: 8, fontSize: 13, lineHeight: 1.6, maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 8 }}>{selectedDoc.content}</pre>
                </details>
              </div>
            )}

            <div style={{ display: 'grid', gap: 10 }}>
              {docs.length === 0 && <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>등록된 문서가 없습니다.</div>}
              {docs.map(d => {
                const ci = catIcon(d.category);
                return (
                  <div key={d.id} style={{ background: 'white', borderRadius: 10, padding: '14px 18px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', transition: 'all 0.15s' }}
                    onClick={() => handleViewDoc(d.id)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = TC; e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,71,161,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: ci.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: ci.color, flexShrink: 0 }}>{ci.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.filename}</div>
                      <div style={{ display: 'flex', gap: 8, fontSize: 12, color: '#888', flexWrap: 'wrap' }}>
                        <span style={{ padding: '1px 8px', borderRadius: 8, background: ci.bg, color: ci.color, fontWeight: 500 }}>{d.category}</span>
                        <span>{formatSize(d.file_size)}</span>
                        <span>{d.page_count}p</span>
                        <span>{d.created_at?.split('T')[0]}</span>
                      </div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); handleDeleteDoc(d.id); }}
                      style={{ background: '#ffebee', border: 'none', color: '#c62828', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>삭제</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* === 문서 등록 탭 === */}
        {tab === 'upload' && (
          <div style={{ maxWidth: 700, margin: '0 auto' }}>
            <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e0e0e0', marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 17, color: '#333' }}>문서 등록</h3>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6, display: 'block' }}>분류</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {CATEGORIES.filter(c => c !== '전체').map(c => (
                    <button key={c} onClick={() => setUploadCat(c)} style={{
                      padding: '6px 14px', borderRadius: 8, border: `2px solid ${uploadCat === c ? TC : '#e0e0e0'}`,
                      background: uploadCat === c ? TB : 'white', cursor: 'pointer', fontSize: 13,
                      color: uploadCat === c ? TC : '#666', fontWeight: uploadCat === c ? 600 : 400,
                    }}>{c}</button>
                  ))}
                </div>
              </div>

              {/* 단일 업로드 */}
              <div style={{ border: '2px dashed #bbdefb', borderRadius: 12, padding: 30, textAlign: 'center', marginBottom: 16, background: '#fafbff', cursor: 'pointer' }}
                onClick={() => fileRef.current?.click()}>
                <input ref={fileRef} type="file" onChange={handleUpload} style={{ display: 'none' }}
                  accept=".pdf,.docx,.xlsx,.xls,.txt,.csv,.json,.pptx,.png,.jpg,.jpeg" />
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{marginBottom:12}}>
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke={TC} strokeWidth="2" strokeLinecap="round"/>
                  <path d="M17 8l-5-5-5 5M12 3v12" stroke={TC} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 4 }}>클릭하여 파일 선택</div>
                <div style={{ fontSize: 12, color: '#888' }}>PDF, DOCX, XLSX, TXT, CSV, PPTX, 이미지 지원</div>
              </div>

              {/* 다중 업로드 */}
              <div style={{ textAlign: 'center' }}>
                <button onClick={() => multiFileRef.current?.click()} style={{
                  padding: '10px 24px', borderRadius: 8, border: `1px solid ${TC}`, background: 'white', color: TC, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{verticalAlign:'middle',marginRight:6}}>
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke={TC} strokeWidth="2"/><path d="M17 8l-5-5-5 5M12 3v12" stroke={TC} strokeWidth="2"/>
                  </svg>
                  여러 파일 한번에 등록
                </button>
                <input ref={multiFileRef} type="file" multiple onChange={handleMultiUpload} style={{ display: 'none' }}
                  accept=".pdf,.docx,.xlsx,.xls,.txt,.csv,.json,.pptx,.png,.jpg,.jpeg" />
              </div>
            </div>

            {uploading && (
              <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" style={{animation:'spin 1s linear infinite',marginBottom:12}}>
                  <circle cx="12" cy="12" r="10" stroke={TC} strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round"/>
                </svg>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>문서를 분석하고 있습니다...</div>
                <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>텍스트 추출, 임베딩 생성, 요약 중</div>
              </div>
            )}

            {uploadResult && !uploading && (
              <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0' }}>
                {uploadResult.error ? (
                  <div style={{ color: '#c62828', fontSize: 14 }}>{uploadResult.error}</div>
                ) : uploadResult.results ? (
                  <div>
                    <h4 style={{ margin: '0 0 12px', color: '#333' }}>일괄 등록 결과</h4>
                    {uploadResult.results.map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: r.status === 'ok' ? '#e8f5e9' : '#ffebee', color: r.status === 'ok' ? '#2e7d32' : '#c62828', fontSize: 12, fontWeight: 700 }}>
                          {r.status === 'ok' ? 'V' : 'X'}
                        </span>
                        <span style={{ fontSize: 14, color: '#333' }}>{r.filename}</span>
                        {r.message && <span style={{ fontSize: 12, color: '#c62828' }}>{r.message}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="#2e7d32" strokeWidth="2.5" strokeLinecap="round"/><circle cx="12" cy="12" r="10" stroke="#2e7d32" strokeWidth="2"/></svg>
                      <span style={{ fontSize: 16, fontWeight: 600, color: '#2e7d32' }}>등록 완료</span>
                    </div>
                    <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}><strong>{uploadResult.filename}</strong> ({formatSize(uploadResult.text_length || 0)}, {uploadResult.page_count}페이지, {uploadResult.chunks}청크)</div>
                    {uploadResult.summary && (
                      <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 14, fontSize: 13, lineHeight: 1.7 }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdC}>{uploadResult.summary}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* === Q&A 기록 탭 === */}
        {tab === 'history' && (
          <div>
            {qnaList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Q&A 기록이 없습니다.</div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {qnaList.map(q => (
                  <div key={q.id} style={{ background: 'white', borderRadius: 10, padding: '16px 20px', border: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: TC, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                        <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>Q</span>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{q.question}</div>
                        <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{q.created_at?.replace('T', ' ').slice(0, 16)}</div>
                      </div>
                    </div>
                    <div style={{ marginLeft: 38, background: '#f8f9fa', borderRadius: 8, padding: '12px 14px', fontSize: 13, lineHeight: 1.7, color: '#555', maxHeight: 200, overflow: 'auto' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdC}>{q.answer}</ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default KnowledgeAgent;
